import { getAuth } from "firebase/auth";
import {
  isOrganisationAuthorityCommandsFeatureEnabled,
  isOrgMembershipCommandsFeatureEnabled,
} from "@/config/runtimeConfig";
import {
  createOrganisationLeaveOperationId,
  organisationContextService,
} from "@/services/organisationContextService";
import { reconcileOwnTrustedMembershipAfterMutation } from "@/services/trustedMembershipService";

export const listOwnPrivateOrganisationContexts = (options) =>
  organisationContextService.listOwnOrganisationContexts(options);

const privateContextToDashboardProjection = (context) =>
  Object.freeze({
    publicIdentifier: context.publicIdentifier,
    name: context.name,
    description: context.description || "",
    category: context.category || "",
    logo: null,
    avatarAssetId: context.avatarAssetId,
    visibility: context.visibility,
    joinPolicy: context.joinPolicy,
    allowTrades: context.economyOpen,
    monetary_parameters: Object.freeze({
      coinName: context.currencyName,
      coinSymbol: context.currency,
      coin_name: context.currencyName,
      coin_symbol: context.currency,
      currencyCode: context.currencyCode,
    }),
    membershipStatus: context.membershipStatus,
    membershipRevision: context.membershipRevision,
    revision: context.revision,
  });

const rejectLegacyOrganisationAuthorityAction = () => {
  const error = new Error(
    "This organisation action requires the private authority service."
  );
  error.code = "private-authority-required";
  throw error;
};

const requireOwnActor = (userUID) => {
  const actorUID = getAuth().currentUser?.uid || null;
  if (!actorUID || actorUID !== userUID) {
    const error = new Error(
      "The authenticated account cannot change another user's membership."
    );
    error.code = "permission-denied";
    throw error;
  }
  return actorUID;
};

const LEAVE_OPERATION_PREFIX = "unit.leaveOrganisation.v1";
const JOIN_OPERATION_PREFIX = "unit.joinOrganisation.v1";
const MAX_LEAVE_CLEANUP_ATTEMPTS = 3;
const operationMemory = new Map();

const operationKey = (kind, uid, orgId) => `${kind}:${uid}:${orgId}`;
const operationStorageKey = (prefix, uid, orgId) => `${prefix}.${uid}.${orgId}`;

const readOperationId = ({ kind, prefix, uid, orgId, create }) => {
  const key = operationKey(kind, uid, orgId);
  if (operationMemory.has(key)) return operationMemory.get(key);
  try {
    const stored = sessionStorage.getItem(operationStorageKey(prefix, uid, orgId));
    if (/^[A-Za-z0-9_-]{16,128}$/.test(stored || "")) {
      operationMemory.set(key, stored);
      return stored;
    }
  } catch (_error) {
    // In-memory replay ownership remains available without session storage.
  }
  const operationId = create();
  operationMemory.set(key, operationId);
  try {
    sessionStorage.setItem(operationStorageKey(prefix, uid, orgId), operationId);
  } catch (_error) {
    // The in-memory operation remains stable for this app session.
  }
  return operationId;
};

const clearOperationId = ({ kind, prefix, uid, orgId }) => {
  operationMemory.delete(operationKey(kind, uid, orgId));
  try {
    sessionStorage.removeItem(operationStorageKey(prefix, uid, orgId));
  } catch (_error) {
    // Best effort after a definitive server result.
  }
};

const readLeaveOperationId = (uid, orgId) =>
  readOperationId({
    kind: "leave",
    prefix: LEAVE_OPERATION_PREFIX,
    uid,
    orgId,
    create: createOrganisationLeaveOperationId,
  });

const clearLeaveOperationId = (uid, orgId) =>
  clearOperationId({
    kind: "leave",
    prefix: LEAVE_OPERATION_PREFIX,
    uid,
    orgId,
  });

const createJoinOperationId = () => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return `join_${globalThis.crypto.randomUUID().replaceAll("-", "_")}`;
  }
  if (typeof globalThis.crypto?.getRandomValues === "function") {
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(24));
    return `join_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0")
    ).join("")}`;
  }
  const error = new Error("Secure organisation operations are unavailable.");
  error.code = "failed-precondition";
  throw error;
};

const readJoinOperationId = (uid, orgId) =>
  readOperationId({
    kind: "join",
    prefix: JOIN_OPERATION_PREFIX,
    uid,
    orgId,
    create: createJoinOperationId,
  });

const clearJoinOperationId = (uid, orgId) =>
  clearOperationId({
    kind: "join",
    prefix: JOIN_OPERATION_PREFIX,
    uid,
    orgId,
  });

export default {
  state: {
    userUnits_customOrgs: null,
    selectedCustomOrgOwnerUID: null,
    selectedCustomOrgUID: null,
    selectedCustomOrgRequestGeneration: 0,
    selectedCustomOrgStatus: "idle",
    role: null,
    monetary_parameters: {
      coinName: "Unit",
      coinSymbol: "Ʉ",
    },
  },
  getters: {
    userUnits_customOrgs: (state, _getters, rootState) =>
      state.selectedCustomOrgStatus === "ready" &&
      state.selectedCustomOrgOwnerUID === rootState.userCredentials?.data?.uid &&
      state.selectedCustomOrgOwnerUID === rootState.selectedOrgOwnerUID &&
      state.selectedCustomOrgUID === rootState.selectedOrgUID
        ? state.userUnits_customOrgs
        : null,
    selectedCustomOrgProjection: (state, _getters, rootState) =>
      state.selectedCustomOrgStatus === "ready" &&
      state.selectedCustomOrgOwnerUID === rootState.userCredentials?.data?.uid &&
      state.selectedCustomOrgOwnerUID === rootState.selectedOrgOwnerUID &&
      state.selectedCustomOrgUID === rootState.selectedOrgUID
        ? state.userUnits_customOrgs
        : null,
    role: (state) => state.role,
    monetary_parameters: (state) => state.monetary_parameters,
  },
  mutations: {
    SET_USER_UNITS_CUSTOM_ORGS(state, userUnitsData) {
      state.userUnits_customOrgs = userUnitsData;
    },
    BEGIN_SELECTED_CUSTOM_ORG(state, { userId, orgUID }) {
      state.selectedCustomOrgRequestGeneration += 1;
      state.selectedCustomOrgOwnerUID = userId;
      state.selectedCustomOrgUID = orgUID;
      state.selectedCustomOrgStatus = "pending";
      state.userUnits_customOrgs = null;
    },
    COMPLETE_SELECTED_CUSTOM_ORG(
      state,
      { userId, orgUID, generation, unitData }
    ) {
      if (
        state.selectedCustomOrgOwnerUID !== userId ||
        state.selectedCustomOrgUID !== orgUID ||
        state.selectedCustomOrgRequestGeneration !== generation
      ) {
        return;
      }
      state.userUnits_customOrgs = { [orgUID]: unitData };
      state.selectedCustomOrgStatus = "ready";
    },
    FAIL_SELECTED_CUSTOM_ORG(state, { userId, orgUID, generation }) {
      if (
        state.selectedCustomOrgOwnerUID !== userId ||
        state.selectedCustomOrgUID !== orgUID ||
        state.selectedCustomOrgRequestGeneration !== generation
      ) {
        return;
      }
      state.userUnits_customOrgs = null;
      state.selectedCustomOrgStatus = "error";
    },
    SET_ROLE(state, role) {
      state.role = role || null;
    },
    SET_MONETARY_PARAMS(state, payload) {
      state.monetary_parameters = payload;
    },
    RESET_MONETARY_PARAMS(state) {
      state.monetary_parameters = {
        coinName: "Unit",
        coinSymbol: "Ʉ",
      };
    },
  },
  actions: {
    async fetchMonetaryParameters({ commit }, orgUID) {
      if (!orgUID) {
        commit("RESET_MONETARY_PARAMS");
        return null;
      }
      const context = await organisationContextService.getOwnOrganisationContext(
        orgUID
      );
      if (context?.orgUID !== orgUID || context.membershipStatus !== "active") {
        commit("RESET_MONETARY_PARAMS");
        return null;
      }
      const monetaryParameters = {
        coinName: context.currencyName,
        coinSymbol: context.currency,
        coin_name: context.currencyName,
        coin_symbol: context.currency,
        currencyCode: context.currencyCode,
      };
      commit("SET_MONETARY_PARAMS", monetaryParameters);
      return monetaryParameters;
    },

    async setMonetaryParameters({ commit }, payload) {
      commit("SET_MONETARY_PARAMS", payload);
    },

    async getRoleInfo({ commit }) {
      commit("SET_ROLE", null);
      return null;
    },

    async getRoles() {
      return null;
    },

    async fetchAndCommitUserUnitsCustomOrgs({ dispatch, rootState }, userId) {
      const orgUID = rootState.selectedOrgUID;
      if (
        !userId ||
        rootState.userCredentials?.data?.uid !== userId ||
        rootState.selectedOrgOwnerUID !== userId ||
        !orgUID
      ) {
        return null;
      }
      return dispatch("fetchAndCommitSelectedCustomOrg", { userId, orgUID });
    },

    async fetchAndCommitSelectedCustomOrg(
      { commit, rootState, state },
      { userId, orgUID }
    ) {
      const ownsSelectedContext = () =>
        rootState.userCredentials?.data?.uid === userId &&
        rootState.selectedOrgOwnerUID === userId &&
        rootState.selectedOrgUID === orgUID;
      if (!userId || !orgUID || !ownsSelectedContext()) return null;

      commit("BEGIN_SELECTED_CUSTOM_ORG", { userId, orgUID });
      const generation = state.selectedCustomOrgRequestGeneration;
      try {
        const context =
          await organisationContextService.getOwnOrganisationContext(orgUID);
        if (!ownsSelectedContext()) return null;
        if (context?.orgUID !== orgUID || context.membershipStatus !== "active") {
          throw new Error("Invalid private organisation context");
        }
        const unitData = privateContextToDashboardProjection(context);
        commit("COMPLETE_SELECTED_CUSTOM_ORG", {
          userId,
          orgUID,
          generation,
          unitData,
        });
        return unitData;
      } catch (error) {
        if (ownsSelectedContext()) {
          commit("FAIL_SELECTED_CUSTOM_ORG", { userId, orgUID, generation });
        }
        throw error;
      }
    },

    async joinCustomOrg(_context, payload) {
      const userUID = payload?.userUID;
      const orgUID = payload?.newOrgUID;
      requireOwnActor(userUID);
      if (
        !isOrganisationAuthorityCommandsFeatureEnabled ||
        !orgUID ||
        !Number.isSafeInteger(payload?.expectedRevision) ||
        payload.expectedRevision < 0
      ) {
        return rejectLegacyOrganisationAuthorityAction();
      }
      const { organisationCommandAuthorityService } = await import(
        "@/services/organisationCommandAuthorityService"
      );
      const result = await organisationCommandAuthorityService.joinOrganisation({
        operationId: readJoinOperationId(userUID, orgUID),
        expectedRevision: payload.expectedRevision,
        orgId: orgUID,
      });
      clearJoinOperationId(userUID, orgUID);
      await reconcileOwnTrustedMembershipAfterMutation(orgUID);
      return result;
    },

    async getOrgMembersCount() {
      return rejectLegacyOrganisationAuthorityAction();
    },

    async deleteAccount() {
      return rejectLegacyOrganisationAuthorityAction();
    },

    async leaveCustomOrg({ commit, dispatch }, payload) {
      const userUID = payload?.userUID;
      const orgUID = payload?.orgUID;
      requireOwnActor(userUID);
      if (!orgUID) return rejectLegacyOrganisationAuthorityAction();

      const finalizeServerOwnedLeave = async () => {
        clearLeaveOperationId(userUID, orgUID);
        await dispatch("clearSelectedOrganisationPermissions");
        await dispatch("appearance/forgetOrganisation", { uid: userUID, orgId: orgUID });
        commit("SET_USER_UNITS_CUSTOM_ORGS", null);
        commit("CLEAR_ROLE_DATA");
        await dispatch("ensureSelectedOrg", { userUID, force: true });
        await dispatch("fetchUserOrgCoinSymbol", userUID);
        await reconcileOwnTrustedMembershipAfterMutation(orgUID);
      };

      if (
        isOrganisationAuthorityCommandsFeatureEnabled &&
        typeof payload?.impactRef === "string"
      ) {
        // Account lifecycle v2: the caller presents the impactRef from a fresh
        // previewOwnOrganisationExit and any nominated successors.
        const { accountAdministrationService } = await import(
          "@/services/accountAdministrationService"
        );
        requireOwnActor(userUID);
        const result = await accountAdministrationService.leaveOrganisation({
          orgId: orgUID,
          expectedRevision: payload.expectedRevision,
          impactRef: payload.impactRef,
          successorMembershipRefs: payload.successorMembershipRefs || [],
          ...(payload.operationId ? { operationId: payload.operationId } : {}),
        });
        requireOwnActor(userUID);
        await finalizeServerOwnedLeave();
        return result;
      }

      const operationId = readLeaveOperationId(userUID, orgUID);
      if (isOrganisationAuthorityCommandsFeatureEnabled) {
        const context =
          await organisationContextService.getOwnOrganisationContext(orgUID);
        requireOwnActor(userUID);
        await organisationContextService.leavePrivateOrganisation(
          orgUID,
          context.membershipRevision,
          operationId
        );
        requireOwnActor(userUID);
        await finalizeServerOwnedLeave();
        return;
      }

      if (!isOrgMembershipCommandsFeatureEnabled) {
        return rejectLegacyOrganisationAuthorityAction();
      }

      let result = null;
      for (
        let attempt = 0;
        attempt < MAX_LEAVE_CLEANUP_ATTEMPTS;
        attempt += 1
      ) {
        requireOwnActor(userUID);
        result = await organisationContextService.leaveOwnOrganisation(
          orgUID,
          operationId
        );
        if (result.cleanup === "complete") break;
      }
      if (result?.cleanup !== "complete") {
        const error = new Error(
          "Organisation cleanup is still running. Retry to continue."
        );
        error.code = "cleanup-pending";
        throw error;
      }
      requireOwnActor(userUID);
      await finalizeServerOwnedLeave();
    },

    async acceptInvite() {
      return rejectLegacyOrganisationAuthorityAction();
    },
  },
};
